import React from 'react';
import {
  Bot,
  Briefcase,
  Mail,
  Calendar,
  Phone,
  Database,
  Users,
  BookOpen,
  BarChart3,
  Library,
  UserPlus,
} from 'lucide-react';
import WelcomeBanner from '../ui/WelcomeBanner';
import SectionHeader from '../ui/SectionHeader';
import QuickAccessCard from '../ui/QuickAccessCard';
import SupportCard from '../ui/SupportCard';

const mainTools = [
  {
    id: 'wiki',
    icon: Library,
    title: 'Sonneil Wiki',
    description: 'Base de conocimiento',
    href: 'https://help.sonneil.com',
    color: 'orange',
  },
  {
    id: 'salesforce',
    icon: Database,
    title: 'Salesforce CRM',
    description: 'CRM corporativo',
    href: 'https://sonneil.lightning.force.com',
    color: 'blue',
  },
  {
    id: 'aircall',
    icon: Phone,
    title: 'Aircall',
    description: 'Plataforma de llamadas',
    href: 'https://app.aircall.io',
    color: 'green',
  },
  {
    id: 'gmail',
    icon: Mail,
    title: 'Gmail',
    description: 'Correo corporativo',
    href: 'https://mail.google.com',
    color: 'red',
  },
  {
    id: 'calendar',
    icon: Calendar,
    title: 'Google Calendar',
    description: 'Agenda y reuniones',
    href: 'https://calendar.google.com',
    color: 'blue',
  },
  {
    id: 'ai-tools',
    icon: Bot,
    title: 'Herramientas IA',
    description: 'Suite de IA Sonneil',
    color: 'primary',
  },
  {
    id: 'client-referral',
    icon: UserPlus,
    title: 'Prescribir cliente',
    description: 'Recomienda clientes',
    color: 'primary',
  },
];

const quickResources = [
  {
    id: 'wiki',
    icon: Library,
    title: 'Sonneil Wiki',
    href: 'https://help.sonneil.com',
  },
  {
    id: 'manual-salesforce',
    icon: BookOpen,
    title: 'Manual Salesforce',
    href: 'https://drive.google.com/file/d/1W93QNyOx83v5otMYovYuUk4cQeYT1SzA/view?usp=drive_link',
  },
  {
    id: 'manual-aircall',
    icon: BookOpen,
    title: 'Manual Aircall',
    href: 'https://drive.google.com/file/d/1-Tk6qQ5IL9quvCmPVLLD2B6L4obBV0FC/view?usp=drive_link',
  },
  {
    id: 'capacitacion',
    icon: BarChart3,
    title: 'Formación Aircall',
    href: 'https://docs.google.com/presentation/d/e/2PACX-1vRF1pZ__zECYtjEqp1DnG3zIX8ioKiFNYduPWyoHW_2yGbSVAU7KKpex6P9LS70Dbi2xp-Frcqe2o5A/pubembed?start=false&loop=false&delayms=3000',
  },
];

const HomePage = ({ user, onNavigate }) => {
  return (
    <div className="animate-stagger">
      <WelcomeBanner userName={user?.displayName} />

      {/* Quick Access Tools */}
      <section className="section">
        <SectionHeader
          title="Herramientas Principales"
          description="Acceso rápido a las aplicaciones más utilizadas"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {mainTools.map((tool) => (
            <QuickAccessCard
              key={tool.id}
              icon={tool.icon}
              title={tool.title}
              description={tool.description}
              href={tool.href}
              onClick={
                tool.id === 'ai-tools'
                  ? () => onNavigate('/herramientas-ia')
                  : tool.id === 'client-referral'
                  ? () => onNavigate('/crm-ventas#prescribir-cliente')
                  : undefined
              }
              color={tool.color}
            />
          ))}
        </div>
      </section>

      {/* Quick Resources */}
      <section className="section mt-8">
        <SectionHeader
          title="Recursos Rápidos"
          description="Manuales y documentación frecuente"
          action
          actionText="Ver biblioteca completa"
          onActionClick={() => onNavigate('/formacion')}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickResources.map((resource) => (
            <a
              key={resource.id}
              href={resource.href}
              target="_blank"
              rel="noopener noreferrer"
              className="card p-4 flex items-center gap-3 hover:border-primary/30 border border-transparent transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary-lighter/50 flex items-center justify-center">
                <resource.icon size={18} className="text-primary" />
              </div>
              <span className="text-sm font-medium text-text-primary group-hover:text-primary transition-colors">
                {resource.title}
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* Additional Sections */}
      <section className="section mt-8">
        <SectionHeader
          title="Accesos por Departamento"
          description="Herramientas organizadas por área"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => onNavigate('/crm-ventas')}
            className="card p-5 text-left hover:border-primary/30 border border-transparent transition-all group"
          >
            <Briefcase size={24} className="text-primary mb-3" />
            <h4 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
              CRM y Ventas
            </h4>
            <p className="text-sm text-text-secondary mt-1">
              Salesforce, Aircall y herramientas comerciales
            </p>
          </button>

          <button
            onClick={() => onNavigate('/herramientas-corporativas')}
            className="card p-5 text-left hover:border-primary/30 border border-transparent transition-all group"
          >
            <Mail size={24} className="text-info mb-3" />
            <h4 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
              Herramientas Corporativas
            </h4>
            <p className="text-sm text-text-secondary mt-1">
              Google Workspace, correo y productividad
            </p>
          </button>

          <button
            onClick={() => onNavigate('/recursos-humanos')}
            className="card p-5 text-left hover:border-primary/30 border border-transparent transition-all group"
          >
            <Users size={24} className="text-success mb-3" />
            <h4 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
              Recursos Humanos
            </h4>
            <p className="text-sm text-text-secondary mt-1">
              PayFit, horarios y gestión de personal
            </p>
          </button>

          <button
            onClick={() => onNavigate('/formacion')}
            className="card p-5 text-left hover:border-primary/30 border border-transparent transition-all group"
          >
            <BookOpen size={24} className="text-warning mb-3" />
            <h4 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
              Formación
            </h4>
            <p className="text-sm text-text-secondary mt-1">
              Manuales, presentaciones y capacitación
            </p>
          </button>
        </div>
      </section>

      {/* Support Card */}
      <section className="section mt-8">
        <div className="max-w-md">
          <SupportCard />
        </div>
      </section>
    </div>
  );
};

export default HomePage;


